import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { toast } from "$global-providers/toast-provider";
import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { RxDocument } from "rxdb";
import { useRxData } from "rxdb-hooks";
import { v4 as uuidv4 } from "uuid";
import type {
  DiagnosisCode,
  EpocusBilling,
  ProcedureCode,
} from "../../../../../../global-providers/rxdb-provider/collections/epocus-billing";
import { useRxCollectionEpocusBilling } from "../../../../../../global-providers/rxdb-provider/collections/epocus-billing";
import type { EpocusEncounter } from "../../../../../../global-providers/rxdb-provider/collections/epocus-encounter";
import {
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
  useRxCollectionEpocusEncounter,
} from "../../../../../../global-providers/rxdb-provider/collections/epocus-encounter";
import type { EpocusExam } from "../../../../../../global-providers/rxdb-provider/collections/epocus-exam";
import {
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
  useRxCollectionEpocusExam,
} from "../../../../../../global-providers/rxdb-provider/collections/epocus-exam";
import type { ExamWorksheetData } from "../../../../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { useUser } from "../../../../../../global-providers/user-provider/provider";
import { BillingCptChip } from "../../../../_lib/billing/BillingCptChip";
import { BillingCptConfirmationSheet } from "../../../../_lib/billing/BillingCptConfirmationSheet";
import {
  BillingCptOverrideSheet,
  type OverrideResult,
} from "../../../../_lib/billing/BillingCptOverrideSheet";
import {
  defaultBillingMode,
  suggestCptCodes,
  suggestDiagnosisCodes,
  toDocumentationStatus,
  toStoredBillingMode,
  validateDocumentation,
} from "../../../../_lib/billing/epocus-billing-engine";
import type {
  CptSuggestion,
  IcdSuggestion,
} from "../../../../_lib/billing/types";
import { assignReviewer } from "../../../../_lib/demo-assignments";
import { isPresetConflict } from "../../../../_lib/dicom-preset-mapping";
import {
  EXAM_ICONS,
  EXAM_LABELS,
} from "../../../../_lib/epocus-exam-constants";
import { AlertTriangle } from "../../../../_lib/epocus-icons";
import { EpocusImageGallery } from "../../../../_lib/epocus-image-gallery";
import {
  EPOCUS_ICON_ACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_SELECTED_BG,
  EPOCUS_STATUS_IMPORTED_BG,
  EPOCUS_STATUS_IMPORTED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_TO_DOC_BORDER,
  rem,
} from "../../../../_lib/epocus-palette";
import { epocusTheme } from "../../../../_lib/epocus-theme-provider";
import {
  collectAssignedExamImageIds,
  imagingInstanceToExamImage,
  useAssignedExamImageIds,
  useImagingInstancesByAccessionNumber,
} from "../../../../_lib/hooks/use-encounter-imaging-instances";
import { useEpocusExamImages } from "../../../../_lib/hooks/use-epocus-exam-images";
import { RoleGate } from "../../../../_lib/rbac/role-gate";
import { useCan } from "../../../../_lib/rbac/use-can";
import { findReviewer } from "../../../../_lib/reviewers";
import { useAllNormal } from "../../../../_lib/use-all-normal";
import {
  AortaWorksheet,
  isAortaComplete,
} from "../../../../_lib/worksheets/aorta-worksheet";
import {
  BladderWorksheet,
  isBladderComplete,
} from "../../../../_lib/worksheets/bladder-worksheet";
import {
  CardiacWorksheet,
  isCardiacComplete,
} from "../../../../_lib/worksheets/cardiac-worksheet";
import {
  EfastWorksheet,
  isEfastComplete,
} from "../../../../_lib/worksheets/efast-worksheet";
import {
  isLungComplete,
  LungWorksheet,
} from "../../../../_lib/worksheets/lung-worksheet";
import {
  isPregnancyComplete,
  PregnancyWorksheet,
} from "../../../../_lib/worksheets/pregnancy-worksheet";
import {
  isRenalComplete,
  RenalWorksheet,
} from "../../../../_lib/worksheets/renal-worksheet";
import { useWorksheetAutoSave } from "../../../../_lib/worksheets/use-worksheet-auto-save";
import { WorksheetActions } from "../../../../_lib/worksheets/worksheet-primitives";
import { linkToEpocusEncounter } from "../../../../links";
import {
  isLastExamToSubmit,
  submitEncounterForReview,
} from "../../_lib/encounter-submission";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 960,
  margin: "0 auto",
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const PerformerLockNotice = styled(Typography)({
  backgroundColor: EPOCUS_STATUS_IMPORTED_BG,
  color: EPOCUS_STATUS_IMPORTED_TEXT,
  border: `1px solid ${EPOCUS_TO_DOC_BORDER}`,
  borderRadius: 10,
  padding: "12px 14px",
  fontSize: rem(13),
  fontWeight: 600,
});

// Deep-clone exam worksheet data bypassing the RxDB Proxy.
// In dev/acceptance environments RxDB v15 wraps document fields in a Proxy
// that violates V8's invariants when stringified. Reading from _data directly
// (the frozen plain object) avoids the Proxy entirely.
const cloneWorksheetData = (exam: RxDocument<EpocusExam>): ExamWorksheetData =>
  JSON.parse(JSON.stringify(exam._data.worksheetData)) as ExamWorksheetData;

const isExamComplete = (data: ExamWorksheetData): boolean => {
  switch (data.examType) {
    case "cardiac":
      return isCardiacComplete(data);
    case "efast":
      return isEfastComplete(data);
    case "lung":
      return isLungComplete(data);
    case "bladder":
      return isBladderComplete(data);
    case "renal":
      return isRenalComplete(data);
    case "aorta":
      return isAortaComplete(data);
    case "pregnancy":
      return isPregnancyComplete(data);
  }
};

export const EpocusExamPage: FC = () => {
  const { encounterId, examId } = useParams<{
    encounterId: string;
    examId: string;
  }>();
  const navigate = useNavigate();
  const examCollection = useRxCollectionEpocusExam();
  const billingCollection = useRxCollectionEpocusBilling();
  const encounterCollection = useRxCollectionEpocusEncounter();
  const user = useUser();

  const [billingCodesEnabled, { loading: billingCodesLoading }] =
    useFeatureFlag(featureFlags.epocus.billingCodes);

  const { result: exams, isFetching } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: { id: examId ?? "", encounterId: encounterId ?? "" },
      }),
  );
  const exam = exams?.[0] ?? null;

  const { result: encounterExams } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    (collection) =>
      collection.find({ selector: { encounterId: encounterId ?? "" } }),
  );

  const { result: encounters } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    (collection) => collection.findOne(encounterId ?? ""),
  );
  const encounter = encounters?.[0] ?? null;

  // Track existing billing record for this exam (for locked state + idempotency)
  const [existingBilling, setExistingBilling] = useState<EpocusBilling | null>(
    null,
  );
  useEffect(() => {
    if (!billingCollection || !examId) return;
    const sub = billingCollection
      .findOne({ selector: { examId } })
      .$.subscribe((doc) => {
        setExistingBilling((doc?.toJSON() as EpocusBilling) ?? null);
      });
    return () => sub.unsubscribe();
  }, [billingCollection, examId]);

  const isBillingLocked = existingBilling?.locked ?? false;

  // Stable reference prevents useEpocusExamImages from firing on every render
  // when exam is null (loading), which would cause an infinite re-render loop.
  const examImages = useMemo(() => exam?.images ?? [], [exam]);
  const hydratedImages = useEpocusExamImages(examImages);
  const [galleryIndex, setGalleryIndex] = useState(0);

  // PNE-212: Load ImagingInstances for this encounter and auto-import any that
  // haven't yet been added to exam.images (handles DICOM arriving after exam creation).
  const { instances: imagingInstances, dominantPreset } =
    useImagingInstancesByAccessionNumber(encounter?.accessionNumber);
  const { assignedImageIds, isFetching: assignedFetching } =
    useAssignedExamImageIds(encounterId);
  useEffect(() => {
    if (!examCollection || !exam || imagingInstances.length === 0) return;
    // Only the clinician who signed a report takes late images onto it.
    if (exam.signedAt && exam.signedByUserId !== user.id) return;
    // An unsettled claim query looks like "nothing is claimed", which would
    // re-import a sibling's images.
    if (assignedFetching) return;
    let isActive = true;
    const newInstances = imagingInstances.filter(
      (inst) => !assignedImageIds.has(inst.id),
    );
    if (newInstances.length === 0) return;
    const newImages = newInstances.map(imagingInstanceToExamImage);
    const importImages = async () => {
      try {
        const doc = await examCollection.findOne(exam.id).exec();
        if (!isActive || !doc) return;
        // Re-read every exam in the encounter, not just this one: an instance
        // claimed by a sibling between effect setup and here must not be
        // claimed again, or the image ends up on two exams.
        const siblings = await examCollection
          .find({ selector: { encounterId: exam.encounterId } })
          .exec();
        const claimedIds = collectAssignedExamImageIds(
          siblings.map((sibling) => sibling._data as EpocusExam),
        );
        const stillNew = newImages.filter((img) => !claimedIds.has(img.id));
        if (stillNew.length === 0) return;
        // Use the fresh doc's images as the base to avoid overwriting
        // concurrent changes that arrived between effect setup and here.
        const latestImages = doc._data.images as typeof newImages;
        await doc.incrementalPatch({ images: [...latestImages, ...stillNew] });
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to import imaging instances into exam", error);
      }
    };
    void importImages();
    return () => {
      isActive = false;
    };
  }, [
    imagingInstances,
    assignedImageIds,
    assignedFetching,
    exam,
    examCollection,
    user.id,
  ]);

  // PNE-212: Block submission when the dominant DICOM preset conflicts with the exam type.
  // Uses the dominant (most common) preset to avoid false positives from stray images.
  const conflictingPreset = useMemo(() => {
    if (!exam || !dominantPreset) return null;
    return isPresetConflict(dominantPreset, exam.examType)
      ? dominantPreset
      : null;
  }, [dominantPreset, exam]);

  const [worksheetData, setWorksheetData] = useState<ExamWorksheetData | null>(
    null,
  );

  // ─── Billing flow state ───
  const [confirmSheetOpen, setConfirmSheetOpen] = useState(false);
  const [overrideSheetOpen, setOverrideSheetOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingCptCodes, setPendingCptCodes] = useState<ProcedureCode[]>([]);
  const [pendingDiagCodes, setPendingDiagCodes] = useState<DiagnosisCode[]>([]);
  const [cptSuggestions, setCptSuggestions] = useState<CptSuggestion[]>([]);
  const [icdSuggestion, setIcdSuggestion] = useState<IcdSuggestion>({
    code: "",
    description: "",
  });

  // Stable plain-object copy of the persisted worksheet data, recomputed only
  // when the exam identity changes. We cannot fall back to exam.worksheetData
  // directly — in dev/acceptance environments RxDB v15 returns a Proxy for
  // nested objects, which violates V8's non-configurable property invariant
  // when JSON.stringify'd inside the auto-save cleanup flush.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on exam identity; exam._data is intentionally omitted so we don't re-snapshot over in-progress user edits
  const persistedData = useMemo<ExamWorksheetData | null>(
    () => (exam ? cloneWorksheetData(exam) : null),
    [exam?.id],
  );

  // Use the user's local edits when available; otherwise fall back to the
  // persisted plain-object snapshot. This means currentData is non-null on the
  // same render that exam loads, eliminating the extra "Exam not found" flash
  // that occurred when exam was available but worksheetData hadn't been seeded.
  const currentData = worksheetData ?? persistedData;

  const handleWorksheetChange = useCallback((data: ExamWorksheetData) => {
    setWorksheetData(data);
  }, []);

  const isSubmitted = exam?.status === "submitted";

  // Reviewers and program oversight may open an exam to read it, but must not
  // edit it (PNE-533). `readOnly` covers both reasons the worksheet locks —
  // already submitted, or this role can't author — and gates auto-save too, so
  // merely viewing an exam never writes to it.
  const canEditExam = useCan("editOwnExam");

  // An encounter with no performer is still unclaimed, and documenting it is
  // what claims it — so only a performer already set to somebody else locks the
  // worksheet. The backend performer-lock rejects the write regardless; this
  // stops the user filling in a form whose submission can never land.
  const lockedToOtherPerformer =
    !!encounter?.performerId && encounter.performerId !== user.id;
  const readOnly = isSubmitted || !canEditExam || lockedToOtherPerformer;

  const { cancel: cancelAutoSave } = useWorksheetAutoSave(
    examCollection,
    exam?.id ?? null,
    currentData,
    readOnly,
  );

  const { isAllNormal, toggle: toggleAllNormal } = useAllNormal({
    currentData,
    onSetNormal: (normalData) => setWorksheetData(normalData),
    onClear: (emptyData) => setWorksheetData(emptyData),
  });

  const isComplete = currentData ? isExamComplete(currentData) : false;

  // Shared helper — keeps live chip and handleSubmit in sync on engine inputs.
  // Returns all suggested CPT codes for the current worksheet state.
  const computeCptSuggestions = useCallback((data: ExamWorksheetData) => {
    const clinicalSetting = "ED" as const;
    const billingMode = defaultBillingMode(clinicalSetting);
    return suggestCptCodes(data.examType, clinicalSetting, billingMode, data);
  }, []);

  const liveCptSuggestion = useMemo(() => {
    if (!currentData) return null;
    const suggestions = computeCptSuggestions(currentData);
    const primary = suggestions[0];
    if (!primary) return null;
    const extraCount = suggestions.length - 1;
    return {
      code: primary.procedureCode.cpt,
      modifier: primary.procedureCode.modifier,
      label: extraCount > 0 ? `Suggested codes (+${extraCount})` : undefined,
    };
  }, [currentData, computeCptSuggestions]);

  const handleSaveDraft = async () => {
    if (!examCollection || !exam || !currentData) return;
    const doc = await examCollection.findOne(exam.id).exec();
    if (!doc) {
      toast.error("Could not save — exam not found");
      return;
    }
    await doc.incrementalPatch({ worksheetData: currentData });
    toast.success("Draft saved");
  };

  const advanceEncounterIfLastExam = async () => {
    if (!exam || !encounter) return;
    if (!isLastExamToSubmit(encounterExams, exam.id)) return;
    try {
      await submitEncounterForReview(
        encounterCollection,
        encounter.id,
        user.id,
      );
    } catch (err) {
      console.error("Submit encounter for review failed:", err);
    }
  };

  // Only called when epocus.billing-codes is off — see handleSubmit below.
  const handleSubmitWithoutBilling = async () => {
    if (!examCollection || !exam || !currentData || !encounter || isSubmitting)
      return;

    setIsSubmitting(true);
    try {
      const doc = await examCollection.findOne(exam.id).exec();
      if (!doc) {
        toast.error("Exam not found. Please try again.");
        return;
      }

      cancelAutoSave();
      await doc.incrementalPatch({
        worksheetData: currentData,
        status: "submitted",
      });

      await advanceEncounterIfLastExam();

      navigate(linkToEpocusEncounter(encounter.id));
    } catch {
      toast.error("Failed to submit exam. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 1: User taps "Add to Review Queue" — run billing engine, open confirmation sheet
  const handleSubmit = async () => {
    if (!exam || !currentData) return;

    if (!currentData.indication) {
      toast.error("Please select a clinical indication before submitting.");
      return;
    }

    if ((exam.images?.length ?? 0) === 0) {
      toast.error(
        "Images required — add at least one image or clip before submitting.",
      );
      return;
    }

    if (!billingCodesEnabled) {
      await handleSubmitWithoutBilling();
      return;
    }

    const engineResults = computeCptSuggestions(currentData);

    if (engineResults.length === 0) {
      toast.error(
        "Billing blocked — complete the required worksheet fields before submitting.",
      );
      return;
    }

    const diagCodes = suggestDiagnosisCodes(currentData.indication);
    if (diagCodes.length === 0) {
      toast.error(
        "No ICD-10 code found for the selected indication. Please select a different indication.",
      );
      return;
    }

    setPendingCptCodes(engineResults.map((r) => r.procedureCode));
    setPendingDiagCodes(diagCodes);
    setCptSuggestions(
      engineResults.map((r) => ({
        code: r.procedureCode.cpt,
        modifier: r.procedureCode.modifier,
        units: r.procedureCode.units,
        description: r.description,
        payerRiskNote: r.payerRiskNote,
        completenessWarning: r.completenessWarning,
      })),
    );
    setIcdSuggestion({
      code: diagCodes[0].code,
      description: diagCodes[0].description,
    });
    setConfirmSheetOpen(true);
  };

  // Demo-only feature — hard-coded reviewer list, not wired to a real backend.
  // Gated off in production behind epocus.assign-to-reviewer (PNE-552).
  const [assignToReviewerEnabled] = useFeatureFlag(
    featureFlags.epocus.assignToReviewer,
  );

  // Demo-only: skip billing flow, record the assignment in a localStorage overlay,
  // and move the encounter to pending_review so the assigned reviewer sees it.
  // Intentionally does NOT submit the exam — billing is handled by the reviewer/admin.
  const handleAssignToReviewer = async (reviewerId: string) => {
    if (!encounterCollection || !exam || !currentData || !encounter) return;
    const reviewer = findReviewer(reviewerId);
    if (!reviewer) return;

    if (!currentData.indication) {
      toast.error("Please select a clinical indication before submitting.");
      return;
    }
    if ((exam.images?.length ?? 0) === 0) {
      toast.error(
        "Images required — add at least one image or clip before submitting.",
      );
      return;
    }
    if (!isComplete) return;

    try {
      assignReviewer(encounter.id, reviewerId);
      const doc = await encounterCollection.findOne(encounter.id).exec();
      // Flip to pending_review so the queue picks it up. Uses existing schema
      // field — no replication risk. Assignment itself stays in localStorage.
      if (doc && doc._data.status !== "pending_review") {
        await doc.incrementalPatch({ status: "pending_review" });
      }
      toast.success(`Sent to ${reviewer.name}`);
      navigate(linkToEpocusEncounter(encounter.id));
    } catch {
      toast.error("Failed to assign reviewer. Please try again.");
    }
  };

  // Step 2: User confirms suggested codes — save worksheet + create billing record
  // Order: submit exam first (critical user action), then persist billing.
  // If billing fails, the exam is still correctly submitted and billing can be
  // retried or created manually — preferable to the reverse partial-failure case.
  const handleBillingConfirm = async () => {
    if (
      !examCollection ||
      !billingCollection ||
      !exam ||
      !currentData ||
      !encounter ||
      isSubmitting
    )
      return;

    // Use the encounter's performer when set; otherwise fall back to the
    // currently logged-in user (e.g. for accession-only encounters that
    // haven't yet been claimed by a specific performer).
    const performerId = encounter.performerId ?? user.id;
    setIsSubmitting(true);

    try {
      // Submit the exam worksheet first
      const doc = await examCollection.findOne(exam.id).exec();
      if (!doc) {
        toast.error("Exam not found. Please try again.");
        return;
      }

      cancelAutoSave();

      await doc.incrementalPatch({
        worksheetData: currentData,
        status: "submitted",
      });

      // Then persist the billing record
      const clinicalSetting = "ED" as const;
      const billingMode = defaultBillingMode(clinicalSetting);
      const validation = validateDocumentation({
        indication: currentData.indication,
        imagesStored: (exam.images?.length ?? 0) > 0,
        findingsPopulated: isComplete,
      });

      const confirmedCodes = pendingCptCodes.map((code) => ({
        ...code,
        performerConfirmed: true,
      }));

      // Re-use existing billing record if one already exists for this exam
      const existing = await billingCollection
        .findOne({ selector: { examId: exam.id } })
        .exec();

      // Don't overwrite a locked/exported billing record
      if (existing?.locked) {
        toast.error("Billing record is locked and cannot be modified.");
        setConfirmSheetOpen(false);
        return;
      }

      const existingData = existing?.toJSON() as EpocusBilling | undefined;
      const now = new Date().toISOString();
      const billingRecord: EpocusBilling = {
        id: existingData?.id ?? uuidv4(),
        examId: exam.id,
        encounterId: encounter.id,
        dateOfService: encounter.date,
        patientMrn: encounter.elephantPatientId ?? null,
        patientDob: existingData?.patientDob ?? null,
        payerName: existingData?.payerName ?? null,
        policyNumber: existingData?.policyNumber ?? null,
        performingPhysicianName: existingData?.performingPhysicianName ?? null,
        performingPhysicianNpi: existingData?.performingPhysicianNpi ?? null,
        supervisingPhysicianNpi: existingData?.supervisingPhysicianNpi ?? null,
        facilityNpi: existingData?.facilityNpi ?? null,
        clinicalSetting,
        billingMode: toStoredBillingMode(billingMode),
        procedureCodes: confirmedCodes,
        diagnosisCodes: pendingDiagCodes,
        documentationStatus: toDocumentationStatus(validation.status),
        imagesStored: (exam.images?.length ?? 0) > 0,
        exportedAt: existingData?.exportedAt ?? null,
        locked: existingData?.locked ?? false,
        meta: {
          createdAt: existingData?.meta?.createdAt ?? now,
          updatedAt: now,
          createdByUserId: existingData?.meta?.createdByUserId ?? performerId,
          updatedByUserId: performerId,
        },
      };

      await billingCollection.upsert(billingRecord);

      await advanceEncounterIfLastExam();

      setConfirmSheetOpen(false);
      navigate(linkToEpocusEncounter(encounter.id));
    } catch {
      toast.error("Failed to submit billing. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 3: User overrides — replace suggested code, then confirm
  const handleOverride = () => {
    setConfirmSheetOpen(false);
    setOverrideSheetOpen(true);
  };

  const handleOverrideSubmit = (result: OverrideResult) => {
    const overriddenCode: ProcedureCode = {
      cpt: result.cpt,
      modifier: result.modifier,
      units: result.units,
      systemSuggested: false,
      performerConfirmed: true,
      overrideReason: result.reason,
    };
    setPendingCptCodes([overriddenCode]);
    setCptSuggestions([
      {
        code: result.cpt,
        modifier: result.modifier,
        units: result.units,
        description: result.description,
      },
    ]);
    setOverrideSheetOpen(false);
    setConfirmSheetOpen(true);
  };

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!exam || !currentData) {
    return (
      <PageRoot>
        <Typography>Exam not found.</Typography>
      </PageRoot>
    );
  }

  const imageGallery = (
    <>
      {conflictingPreset && (
        <Box
          role="alert"
          style={{
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 8,
            padding: "10px 12px",
          }}
        >
          <Typography
            style={{ color: "#dc2626", fontWeight: 600, fontSize: rem(13) }}
          >
            <AlertTriangle
              width={15}
              height={15}
              fill="currentColor"
              style={{ verticalAlign: "text-bottom", marginRight: 4 }}
            />
            Images captured in <strong>{conflictingPreset}</strong> preset —
            this doesn&apos;t match the{" "}
            <strong>{EXAM_LABELS[exam.examType]}</strong> exam type. Check you
            have the right study, or add a different exam.
          </Typography>
        </Box>
      )}
      <EpocusImageGallery
        images={hydratedImages}
        selectedIndex={galleryIndex}
        onImageClick={setGalleryIndex}
      />
    </>
  );

  const worksheetProps = {
    isAllNormal,
    onToggleAllNormal: toggleAllNormal,
    readOnly,
    imageGallery,
  };

  const ExamIcon = exam ? EXAM_ICONS[exam.examType] : null;

  return (
    <PageRoot>
      <Button
        onClick={() => navigate(linkToEpocusEncounter(encounterId ?? ""))}
        style={{
          color: EPOCUS_PRIMARY,
          textTransform: "none",
          fontSize: rem(14),
          padding: "4px 0",
          minWidth: 0,
          alignSelf: "flex-start",
        }}
      >
        ‹ Back to encounter
      </Button>

      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          <Box
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              backgroundColor: EPOCUS_SELECTED_BG,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
            aria-hidden
          >
            {ExamIcon && (
              <ExamIcon width={28} height={28} fill={EPOCUS_ICON_ACTIVE} />
            )}
          </Box>
          <Box>
            <Typography
              variant="h5"
              style={{
                fontWeight: 700,
                color: EPOCUS_TEXT_PRIMARY,
                fontSize: rem(20),
              }}
            >
              {EXAM_LABELS[exam.examType]} Worksheet
            </Typography>
            <Typography
              variant="body2"
              style={{ color: EPOCUS_TEXT_SECONDARY, fontSize: rem(13) }}
            >
              {isSubmitted ? "Submitted" : "Draft"}
            </Typography>
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          style={{ gap: 6 }}
        >
          {billingCodesEnabled &&
            (isSubmitted && existingBilling?.procedureCodes[0] ? (
              <BillingCptChip
                code={existingBilling.procedureCodes[0].cpt}
                modifier={existingBilling.procedureCodes[0].modifier}
                label="Billing code"
              />
            ) : (
              !isSubmitted &&
              liveCptSuggestion && (
                <BillingCptChip
                  code={liveCptSuggestion.code}
                  modifier={liveCptSuggestion.modifier}
                  label={liveCptSuggestion.label}
                />
              )
            ))}
          {!readOnly && (
            <Typography
              style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
            >
              Auto-saved
            </Typography>
          )}
        </Box>
      </Box>

      {currentData.examType === "cardiac" && (
        <CardiacWorksheet
          data={currentData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}
      {currentData.examType === "efast" && (
        <EfastWorksheet
          data={currentData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}
      {currentData.examType === "lung" && (
        <LungWorksheet
          data={currentData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}
      {currentData.examType === "bladder" && (
        <BladderWorksheet
          data={currentData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}
      {currentData.examType === "renal" && (
        <RenalWorksheet
          data={currentData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}
      {currentData.examType === "aorta" && (
        <AortaWorksheet
          data={currentData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}
      {currentData.examType === "pregnancy" && (
        <PregnancyWorksheet
          data={currentData}
          onChange={handleWorksheetChange}
          {...worksheetProps}
        />
      )}

      {lockedToOtherPerformer && (
        <PerformerLockNotice>
          This scan has been claimed by another performer. You can read the
          worksheet, but only they can complete it.
        </PerformerLockNotice>
      )}

      <RoleGate can="editOwnExam">
        <WorksheetActions
          isComplete={isComplete}
          onSaveDraft={handleSaveDraft}
          onSubmit={handleSubmit}
          onAssignToReviewer={
            assignToReviewerEnabled ? handleAssignToReviewer : undefined
          }
          isSubmitted={isSubmitted}
          // billingCodesLoading: an in-flight flag read defaults to `false`,
          // which would let a submit slip through the no-billing path even
          // when the flag is actually on — block submission until it settles.
          disabled={
            !!conflictingPreset || billingCodesLoading || lockedToOtherPerformer
          }
        />
      </RoleGate>

      {billingCodesEnabled && (
        <>
          <BillingCptConfirmationSheet
            open={confirmSheetOpen}
            onClose={() => setConfirmSheetOpen(false)}
            cptSuggestions={cptSuggestions}
            icd={icdSuggestion}
            onConfirm={handleBillingConfirm}
            onOverride={handleOverride}
            readOnly={isBillingLocked}
            isSubmitting={isSubmitting}
          />

          <BillingCptOverrideSheet
            open={overrideSheetOpen}
            onClose={() => setOverrideSheetOpen(false)}
            onSubmit={handleOverrideSubmit}
            defaultCpt={cptSuggestions[0]?.code ?? ""}
            defaultModifier={cptSuggestions[0]?.modifier ?? null}
            defaultUnits={cptSuggestions[0]?.units ?? 1}
          />
        </>
      )}
    </PageRoot>
  );
};
